import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, QrCode } from 'lucide-react';

// CK-DDMMYY, the company batch code (QP-TR-01), read off the production date.
const batchFromDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return `CK-${String(d.getDate()).padStart(2, '0')}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getFullYear()).slice(-2)}`;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChalkCartons() {
  const [cartons, setCartons] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingCarton, setEditingCarton] = useState(null);
  const [formData, setFormData] = useState({
    batch_number: '',
    product_id: '',
    product_name: '',
    quantity: 0,
    production_date: '',
    expiry_date: '',
    quality_status: 'approved'
  });

  useEffect(() => {
    fetchCartons();
    fetchProducts();
    fetchBatches();
  }, []);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, { headers: authHeaders() });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch products');
    }
  };

  // Batches production has already opened, so a finished batch is picked rather
  // than typed a second time.
  const fetchBatches = async () => {
    try {
      const response = await axios.get(`${API}/production-batches`, { headers: authHeaders() });
      setBatches(response.data || []);
    } catch (error) {
      console.error('Failed to fetch production batches');
    }
  };

  const fetchCartons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chalk-cartons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCartons(response.data);
    } catch (error) {
      toast.error('Failed to fetch cartons');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingCarton) {
        await axios.put(`${API}/chalk-cartons/${editingCarton.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Carton updated successfully');
      } else {
        await axios.post(`${API}/chalk-cartons`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Carton added successfully');
      }
      setOpen(false);
      resetForm();
      fetchCartons();
      fetchBatches();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const resetForm = () => {
    setFormData({
      batch_number: '',
      product_id: '',
      product_name: '',
      quantity: 0,
      production_date: '',
      expiry_date: '',
      quality_status: 'approved'
    });
    setEditingCarton(null);
  };

  const selectProduct = (productId) => {
    const product = products.find((p) => p.id === productId);
    setFormData({ ...formData, product_id: productId, product_name: product?.name || '' });
  };

  // Picking an open batch fills in its number and the day it was produced.
  const selectBatch = (batchNumber) => {
    const batch = batches.find((b) => b.batch_number === batchNumber);
    setFormData({
      ...formData,
      batch_number: batchNumber,
      production_date: batch?.production_date || formData.production_date,
    });
  };

  const openEditDialog = (carton) => {
    setEditingCarton(carton);
    setFormData({
      batch_number: carton.batch_number,
      product_id: carton.product_id || '',
      product_name: carton.product_name,
      quantity: carton.quantity,
      production_date: carton.production_date,
      expiry_date: carton.expiry_date || '',
      quality_status: carton.quality_status
    });
    setOpen(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.approved;
  };

  return (
    <div data-testid="chalk-cartons-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Finished Goods Inventory</h1>
          <p className="text-slate-600 mt-1">Cartons of every product, batch by batch — the counts here are the stock shown in Products Management</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-carton-button" onClick={resetForm}>
              <Plus className="mr-2" size={16} />
              Add Carton Batch
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="carton-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCarton ? 'Edit Carton Batch' : 'Add Carton Batch'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="batch_number">Batch Number *</Label>
                {batches.length > 0 && !editingCarton && (
                  <Select value={batches.some((b) => b.batch_number === formData.batch_number) ? formData.batch_number : ''} onValueChange={selectBatch}>
                    <SelectTrigger data-testid="batch-pick-select">
                      <SelectValue placeholder="Pick a batch production opened" />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.map((batch) => (
                        <SelectItem key={batch.batch_number} value={batch.batch_number}>
                          {batch.batch_number} — {batch.production_date}
                          {batch.purposes.length ? ` · ${batch.purposes[0]}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Input
                  id="batch_number"
                  data-testid="batch-number-input"
                  value={formData.batch_number}
                  placeholder="CK-DDMMYY (auto from production date if empty)"
                  onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product_name">Product *</Label>
                <Select value={formData.product_id} onValueChange={selectProduct}>
                  <SelectTrigger data-testid="product-select">
                    <SelectValue placeholder="Choose a product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Rows booked before the register held product ids keep the name they were saved with. */}
                <Input
                  id="product_name"
                  data-testid="product-name-input"
                  value={formData.product_name}
                  onChange={(e) => setFormData({ ...formData, product_name: e.target.value, product_id: '' })}
                  placeholder="Product name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quantity">Cartons Produced *</Label>
                <Input
                  id="quantity"
                  data-testid="quantity-input"
                  type="number"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                  required
                />
                <p className="text-xs text-slate-500">Cartons this batch put on the shelf. Saving adds them to the product's stock.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="production_date">Production Date *</Label>
                  <Input
                    id="production_date"
                    data-testid="production-date-input"
                    type="date"
                    value={formData.production_date}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        production_date: value,
                        batch_number: formData.batch_number || batchFromDate(value),
                      });
                    }}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiry_date">Expiry Date</Label>
                  <Input
                    id="expiry_date"
                    data-testid="expiry-date-input"
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quality_status">Quality Status *</Label>
                <Select value={formData.quality_status} onValueChange={(value) => setFormData({ ...formData, quality_status: value })}>
                  <SelectTrigger data-testid="quality-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="save-carton-button" type="submit" className="w-full">
                {editingCarton ? 'Update Carton' : 'Add Carton'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Batch Number</TableHead>
              <TableHead>Product Name</TableHead>
              <TableHead>Cartons</TableHead>
              <TableHead>Production Date</TableHead>
              <TableHead>Expiry Date</TableHead>
              <TableHead>Quality Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cartons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500">
                  No cartons found. Add your first batch.
                </TableCell>
              </TableRow>
            ) : (
              cartons.map((carton) => (
                <TableRow key={carton.id} data-testid={`carton-row-${carton.id}`}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-1">
                      {carton.batch_number}
                      {carton.source === 'qr_batch' && (
                        <QrCode size={13} className="text-slate-400" title="Booked automatically when QR codes were generated" />
                      )}
                    </span>
                  </TableCell>
                  <TableCell>{carton.product_name}</TableCell>
                  <TableCell>{carton.quantity}</TableCell>
                  <TableCell>{new Date(carton.production_date).toLocaleDateString()}</TableCell>
                  <TableCell>{carton.expiry_date ? new Date(carton.expiry_date).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(carton.quality_status)} variant="outline">
                      {carton.quality_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`edit-carton-${carton.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(carton)}
                    >
                      <Pencil size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
